import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, HostBinding, Input } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

/**
 * Etiqueta canónica de pestaña de formulario (design-system `.ds`), para usar
 * dentro de `<ng-template mat-tab-label>` en TODOS los formularios con tabs.
 *
 * Modelo:
 * - Siempre: icono identificativo + título.
 * - `invalid` (solo modo edición): el icono de la pestaña se SUSTITUYE por un
 *   warning mientras haya errores de validación sin resolver.
 * - `dirty` (solo modo edición): punto de color = cambios sin guardar.
 * - En modo lectura el consumidor pasa ambos a false → icono + título limpios.
 * - `variant="info"`: pestaña informativa (chip slate) que la distingue del resto
 *   sin estridencias (p. ej. Historial / solo lectura).
 */
@Component({
	selector: "app-ds-tab-label",
	standalone: true,
	imports: [CommonModule, MatIconModule, MatTooltipModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<mat-icon
			class="tlb-ic"
			[class.is-warn]="invalid"
			[matTooltip]="invalid ? invalidTooltip : ''"
			[matTooltipDisabled]="!invalid || !invalidTooltip"
			>{{ invalid ? "error_outline" : icon }}</mat-icon
		>
		<span class="tlb-tx">{{ label }}</span>
		<span
			class="tlb-dot"
			*ngIf="dirty"
			[matTooltip]="dirtyTooltip"
			[matTooltipDisabled]="!dirtyTooltip"
		></span>
	`,
	styles: [
		`
			:host {
				display: inline-flex;
				align-items: center;
				gap: 7px;
			}
			.tlb-ic {
				font-size: 18px;
				width: 18px;
				height: 18px;
				flex: none;
			}
			.tlb-ic.is-warn {
				color: #d98a00;
				cursor: help;
			}
			.tlb-tx {
				white-space: nowrap;
			}
			.tlb-dot {
				width: 8px;
				height: 8px;
				border-radius: 50%;
				background: var(--accent);
				flex: none;
				display: inline-block;
				cursor: help;
			}
			/* Variante informativa (p. ej. Historial): chip slate calmado. */
			:host(.is-info) {
				background: rgba(90, 113, 132, 0.1);
				border: 1px solid rgba(90, 113, 132, 0.22);
				border-radius: 999px;
				padding: 3px 13px;
			}
			:host(.is-info) .tlb-ic,
			:host(.is-info) .tlb-tx {
				color: #5a7184;
			}
		`,
	],
})
export class DsTabLabelComponent {
	/** Icono identificativo de la pestaña (Material icon name). */
	@Input() icon = "tab";
	@Input() label = "";
	/** Cambios sin guardar en el form de la pestaña (solo edición). */
	@Input() dirty = false;
	/** Errores de validación sin resolver (solo edición): reemplaza el icono. */
	@Input() invalid = false;
	@Input() dirtyTooltip = "";
	@Input() invalidTooltip = "";
	/** Variante visual. `info` = pestaña informativa (chip slate), p. ej. Historial. */
	@Input() variant: "default" | "info" = "default";

	@HostBinding("class.is-info") get esInfo(): boolean {
		return this.variant === "info";
	}
}
